import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { isMobileNative, useIsGuestWeb, useUISize } from 'app/utils/web-helpers';

import { useNavigation, useRoute } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { differenceInDays, localeAwareFormat, localeAwareFormatDurationShort } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { useAllServiceCategories } from 'app/network/category';
import { navigateToListing, navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { MessageSectionOthersListings } from 'app/screens/shared/one-listing/components/messages-section';
import { ViewsSectionOthersListings } from 'app/screens/shared/one-listing/components/views-section';
import type { Request } from 'app/types/listing';
import { ListingSubtype, ListingType } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isSameMonth } from 'date-fns';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type DimensionValue, Pressable, View } from 'react-native';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import { useSwipeableReset } from 'app/hooks/use-swipeable-reset';
import Animated, {
  cancelAnimation,
  type SharedValue,
  useAnimatedStyle,
  interpolate,
  Extrapolation,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import Toast from 'react-native-toast-message';
import { CorpoImage } from './corp-image';
import LenderAcceptedLoan from './lender-accepted-loan';
import { getDonationTextForOverlay, ListingThumbnail } from './listing-thumbnail';
import { useDismissListing, useUndismissListing } from 'app/network/listing';
import { Button } from 'app/design/primitives/button';
import {
  type ListingRequestCardHighestProps,
  type ListingRequestCardProps,
} from 'app/types/listing-request-card-props';
import { type PickOptional } from 'app/types/utility';
import { WhiteLabel } from 'app/types/white-labels';
import { ConciergeSignal } from 'app/screens/chat/all-channels/components/channel';
import { useUser } from 'app/network/user';
import { CommentSectionOthersListings } from 'app/screens/shared/one-listing/components/comments-section';
import { showCommentsUI } from 'app/screens/shared/one-listing/components/listing-comments';
import { isWhiteLabel } from 'app/utils/helpers';
import { DONATION_BADGE_BACKGROUND_COLOR, ListingCardModeBadge } from './listing-card-metadata';
import type { Theme } from 'app/shopify';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconClock, IconMapPin, IconX } from '@tabler/icons-react-native';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
const dsTheme = createDSTheme();

type CardStatus = 'urgent' | 'new' | 'found' | 'donation' | 'activeSearch';

interface RequestCardViewVariant {
  dismissal: 'enabled' | 'disabled';
  duplicateDonationBadge: 'hidden' | 'visible';
  listingSubtype: 'hidden' | 'visible';
  modeBadge: 'hidden' | 'visible';
  subtypeBadge: 'hidden' | 'visible';
  user: 'hidden' | 'visible';
}

function getRequestCardViewVariant({
  canDismiss,
  hideDuplicateDonationBadge,
  hideListingsSubtype,
  showHauteGaspesieRequestMode,
  showSubtypeBadge,
  showUser,
}: {
  canDismiss: boolean;
  hideDuplicateDonationBadge: boolean;
  hideListingsSubtype: boolean;
  showHauteGaspesieRequestMode: boolean;
  showSubtypeBadge: boolean;
  showUser: boolean | undefined;
}): RequestCardViewVariant {
  return {
    dismissal: canDismiss ? 'enabled' : 'disabled',
    duplicateDonationBadge: hideDuplicateDonationBadge ? 'hidden' : 'visible',
    listingSubtype: hideListingsSubtype ? 'hidden' : 'visible',
    modeBadge: showHauteGaspesieRequestMode ? 'visible' : 'hidden',
    subtypeBadge: showSubtypeBadge ? 'visible' : 'hidden',
    user: showUser ? 'visible' : 'hidden',
  };
}

function getListingRequestCardBodyVariant({
  isDismissAnimationActive,
  isRecommendation,
  ...viewVariantProps
}: Parameters<typeof getRequestCardViewVariant>[0] & {
  isDismissAnimationActive: boolean;
  isRecommendation: boolean;
}): RequestCardViewVariant & {
  animation: 'dismissing' | 'idle';
  layout: 'recommendation' | 'standard';
} {
  return {
    animation: isDismissAnimationActive ? 'dismissing' : 'idle',
    layout: isRecommendation ? 'recommendation' : 'standard',
    ...getRequestCardViewVariant(viewVariantProps),
  };
}

function RequestCardDistance({ distance }: { distance?: number }) {
  const { formatDistanceWithoutText } = useDistanceUnit();
  if (distance === undefined) return null;
  return (
    <Row alignItems="center" gap="spacing.general.x0,5">
      <IconMapPin strokeWidth={ICON_STROKE_WIDTH} size={16} color={dsTheme.colors['icon.secondary']} />
      <Text color="text.secondary" variant="bodySmall">
        {formatDistanceWithoutText(distance)}
      </Text>
    </Row>
  );
}

const DESKTOP_DISMISS_FADE_DURATION = 180;
const MOBILE_DISMISS_COLLAPSE_DURATION = 220;
const LANDING_PAGE_DISMISS_COLLAPSE_DURATION = 120;

function getCardStatus({
  subtype,
  status,
  start,
  end,
  createdAt,
  hasLender,
  isDonation: donationFlag,
  transactionMode,
}: PickOptional<Request, 'subtype' | 'status' | 'start' | 'end' | 'createdAt' | 'isDonation' | 'transactionMode'> & {
  hasLender?: boolean;
}): CardStatus {
  const isDonation = isDonationListing({ isDonation: donationFlag, subtype, transactionMode });
  const hasAcceptedLoan = status === 'loaned' || status === 'was_loaned' || hasLender;
  if (hasAcceptedLoan) return 'found';
  if (isDonation) return 'donation';
  if (!createdAt) return 'activeSearch';
  const now = new Date();
  const daysToStart = start ? differenceInDays(start, now) : null;
  const daysToEnd = end ? differenceInDays(end, now) : null;

  const isTimeBounded = start || end;
  const isUpcoming = daysToStart !== null && daysToStart <= 3;
  const isNotOverYet = end ? daysToEnd !== null && daysToEnd >= 0 : daysToStart !== null && daysToStart >= 0;

  if (isTimeBounded && isUpcoming && isNotOverYet) return 'urgent';
  if (status === 'available' && differenceInDays(now, createdAt) <= 1) return 'new';
  return 'activeSearch';
}

function getBadgeBackgroundColor(cardStatus: CardStatus, theme: Theme, hideListingsSubtype: boolean): string {
  switch (cardStatus) {
    case 'urgent':
      return dsTheme.colors['icon.error'];
    case 'new':
      return dsTheme.colors['surface.brand.default'];
    case 'found':
      return theme.colors['text.positive'];
    case 'donation':
      return isWhiteLabel(WhiteLabel.HauteGaspesie)
        ? hideListingsSubtype
          ? 'white'
          : DONATION_BADGE_BACKGROUND_COLOR
        : dsTheme.colors['surface.brand.secondary-4.subtle'];
    case 'activeSearch':
    default:
      return dsTheme.colors['icon.inverse.primary'];
  }
}

function getBadgeTextColor(cardStatus: CardStatus): 'white' | 'text.primary' {
  return cardStatus === 'urgent' || cardStatus === 'new' || cardStatus === 'found' ? 'white' : 'text.primary';
}

function getSubtypeColor(subtype: Request['subtype']): string {
  return subtype === ListingSubtype.object
    ? dsTheme.colors['surface.brand.secondary-5.subtle']
    : dsTheme.colors['surface.brand.secondary-2.subtle'];
}

function SubtypeBadge({
  subtype,
  theme,
  t,
}: {
  subtype: Request['subtype'];
  theme: Theme;
  t: (key: string) => string;
}) {
  return (
    <Column
      alignItems="center"
      style={{
        marginBottom: 2,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: theme.borderRadii['radius.sm'],
        backgroundColor: getSubtypeColor(subtype),
      }}
    >
      <Text fontWeight="700" variant="bodyBase">
        {t(`subtype.${subtype}`)}
      </Text>
    </Column>
  );
}

export function StatusBadgeContent({
  cardStatus,
  before,
  hideListingsSubtype,
}: {
  cardStatus: CardStatus;
  before: Request['before'];
  hideListingsSubtype: boolean;
}) {
  const { i18key, i18nValues } = getDonationTextForOverlay(before);

  const { t } = useTranslation('all-listings');

  switch (cardStatus) {
    case 'urgent':
      return (
        <Row paddingRight="spacing.general.x2">
          <Text color="white" variant="bodySmallMedium">
            {t('urgent')}
          </Text>
        </Row>
      );
    case 'new':
      return (
        <Row paddingRight="spacing.general.x2">
          <Text color="white" variant="bodySmallMedium">
            {t('new')}
          </Text>
        </Row>
      );
    case 'found':
      return (
        <Row>
          <Text color="white" variant="bodySmallMedium">
            {t('found')}
          </Text>
        </Row>
      );
    case 'donation':
      if (hideListingsSubtype) return null;
      return (
        <Text flexShrink={1} variant="bodySmall">
          <Trans
            components={{ strong: <Text variant="bodySmallMedium" /> }}
            count={i18nValues.count}
            i18nKey={i18key}
            ns="common"
          />
        </Text>
      );
    case 'activeSearch':
    default:
      return (
        <Row paddingRight="spacing.general.x2">
          <Text variant="bodySmallMedium">{t('activeSearch')}</Text>
        </Row>
      );
  }
}

export function StatusBadge({
  cardStatus,
  badgeBg,
  hideListingsSubtype,
  before,
  start,
  end,
  hideDates,
}: {
  cardStatus: CardStatus;
  badgeBg: string;
  hideListingsSubtype: boolean;
  before: Request['before'];
  start: Request['start'];
  end: Request['end'];
  hideDates?: boolean;
}) {
  const theme = useTheme<Theme>();
  return (
    <Row alignItems="flex-start" flex={1}>
      <Row
        flexShrink={1}
        style={{
          backgroundColor: badgeBg,
          paddingVertical: 4,
          paddingHorizontal: 6,
          borderRadius: theme.borderRadii['radius.sm'],
        }}
      >
        <StatusBadgeContent cardStatus={cardStatus} before={before} hideListingsSubtype={hideListingsSubtype} />

        {!hideDates && <DateRange cardStatus={cardStatus} start={start} end={end} />}
      </Row>
    </Row>
  );
}

function DateRange({
  cardStatus,
  start,
  end,
}: {
  cardStatus: CardStatus;
  start: Request['start'];
  end: Request['end'];
}) {
  const textColor = getBadgeTextColor(cardStatus);
  const { t } = useTranslation('all-listings');
  if (cardStatus === 'donation' || cardStatus === 'found') return null;

  if (start && end) {
    const dateText =
      start === end
        ? localeAwareFormat(new Date(start), 'd MMM')
        : t('dateRange', {
            start: isSameMonth(new Date(start), new Date(end))
              ? new Date(start).getDate()
              : localeAwareFormat(new Date(start), 'd MMM'),
            end: localeAwareFormat(new Date(end), 'd MMM'),
          });
    return (
      <Text color={textColor} flexShrink={1} paddingRight="spacing.general.x1" variant="bodySmall">
        {dateText}
      </Text>
    );
  }

  return (
    <Text color={textColor} flexShrink={1} variant="bodySmall">
      {t('noDateRange')}
    </Text>
  );
}

export function useListingRequestCardBadge({
  subtype,
  status,
  start,
  end,
  createdAt,
  hideListingsSubtype,
  hasLender,
  isDonation,
  transactionMode,
}: PickOptional<Request, 'subtype' | 'status' | 'start' | 'end' | 'createdAt' | 'isDonation' | 'transactionMode'> & {
  hideListingsSubtype: boolean;
  hasLender?: boolean;
}) {
  const theme = useTheme<Theme>();
  const { data: serviceCategories } = useAllServiceCategories({ enabled: !hideListingsSubtype });
  const allowsServices = Boolean(serviceCategories?.length);
  const cardStatus = getCardStatus({ subtype, status, start, createdAt, end, hasLender, isDonation, transactionMode });
  const showSubtypeBadge =
    allowsServices && (subtype === ListingSubtype.service || subtype === ListingSubtype.object) && !hideListingsSubtype;
  const badgeBg = getBadgeBackgroundColor(cardStatus, theme, hideListingsSubtype);
  return { cardStatus, showSubtypeBadge, allowsServices, badgeBg };
}

function ListingRequestUserRow({
  user,
  groups,
  theme,
  isCurrentlyUnavailable,
  avatarSize = 30,
  nameNumberOfLines,
  userRowPlacement = 'belowThumbnail',
  lastActiveDisplay = 'withPrefix',
  countsSlot,
  trailingSlot,
}: {
  user: Request['user'];
  groups: Request['groups'];
  theme: Theme;
  isCurrentlyUnavailable?: boolean;
  avatarSize?: number;
  nameNumberOfLines?: number;
  userRowPlacement?: 'belowThumbnail' | 'aboveThumbnail';
  /** `distanceOnly` = only the relative period (e.g. hours/days), no "Active … ago" prefix */
  lastActiveDisplay?: 'withPrefix' | 'distanceOnly';
  /** Views / interaction counters rendered on the right (babillard list cards). */
  countsSlot?: ReactNode;
  /** Extra control on the far right (e.g. dismiss X on desktop vertical cards). */
  trailingSlot?: ReactNode;
}) {
  const { t } = useTranslation(['common', 'neighbour-profile']);
  const navigation = useNavigation();
  const authorizeProfile = useAuthorize('member-profile');
  const isUnavailable = isCurrentlyUnavailable ?? false;

  return (
    <Row
      alignItems="center"
      gap="spacing.general.x2"
      marginBottom={userRowPlacement === 'aboveThumbnail' ? 'spacing.general.x2' : undefined}
      marginTop={userRowPlacement === 'belowThumbnail' ? 'spacing.general.x2' : undefined}
      justifyContent="space-between"
    >
      <Pressable
        accessibilityLabel={t('profile.viewMember', {
          ns: 'accessibility',
          name: `${user.profile.firstName} ${user.profile.lastName}`,
        })}
        accessibilityRole="button"
        onPress={(event) => {
          event.stopPropagation();
          authorizeProfile(() => navigateToUserProfile(navigation, user.id));
        }}
        style={{
          alignItems: 'flex-start',
          flexDirection: 'row',
          flexShrink: 1,
          gap: theme.spacing['spacing.general.x2'],
        }}
      >
        <RequestUserAvatar avatarSize={avatarSize} groups={groups} picture={user.profile.picture} theme={theme} />
        <Column flexShrink={1} gap="spacing.general.x0,5">
          <Text color="text.secondary" flexShrink={1} numberOfLines={nameNumberOfLines} variant="bodySmall">
            {user.profile.firstName} {user.profile.lastName}
          </Text>
          <RequestUserMeta
            isUnavailable={isUnavailable}
            lastActiveDisplay={lastActiveDisplay}
            lastSeenAt={user.lastSeenAt}
            trustPoints={user.trustPoints}
          />
        </Column>
      </Pressable>
      {countsSlot}
      {trailingSlot}
    </Row>
  );
}

function RequestUserAvatar({
  avatarSize,
  groups,
  picture,
  theme,
}: {
  avatarSize: number;
  groups: Request['groups'];
  picture: Request['user']['profile']['picture'];
  theme: Theme;
}) {
  if (!picture) return null;

  return (
    <Row justifyContent="center">
      <Row backgroundColor="surface.inverse.tertiary-neutral" borderRadius="radius.xl">
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          height={avatarSize}
          recyclingKey={`${picture}-card`}
          source={{ uri: getImageVariantUrl(picture, 'mini') }}
          style={{ borderRadius: theme.borderRadii['radius.xl'], objectFit: 'cover' }}
        />
        {groups?.length ? (
          <CorpoImage
            color="none"
            picture={groups[0]?.picture}
            style={{ alignSelf: 'center', width: avatarSize, height: avatarSize }}
          />
        ) : null}
      </Row>
    </Row>
  );
}

function RequestUserMeta({
  isUnavailable,
  lastActiveDisplay,
  lastSeenAt,
  trustPoints,
}: {
  isUnavailable: boolean;
  lastActiveDisplay: 'withPrefix' | 'distanceOnly';
  lastSeenAt: Request['user']['lastSeenAt'];
  trustPoints: Request['user']['trustPoints'];
}) {
  const { t } = useTranslation(['common', 'neighbour-profile']);
  if (!trustPoints && !lastSeenAt && !isUnavailable) return null;

  if (isUnavailable) {
    return (
      <Row alignItems="center" gap="spacing.general.x1">
        {trustPoints ? (
          <Text fontSize={12} color="text.placeholder" numberOfLines={1} variant="bodyBase">
            {`👍 (${trustPoints.toFixed(0)})`}
          </Text>
        ) : null}
        {trustPoints ? (
          <Text fontSize={12} color="text.placeholder" numberOfLines={1} variant="bodyBase">
            |
          </Text>
        ) : null}
        <IconClock strokeWidth={ICON_STROKE_WIDTH} size={12} color={dsTheme.colors['icon.secondary']} />
        <Text color="text.secondary" numberOfLines={1} variant="bodyXSmall">
          {t('currentlyUnavailable', { ns: 'neighbour-profile' })}
        </Text>
      </Row>
    );
  }

  const lastActive = lastSeenAt
    ? lastActiveDisplay === 'distanceOnly'
      ? localeAwareFormatDurationShort(new Date(lastSeenAt))
      : t('lastActiveAgoShort', {
          ago: localeAwareFormatDurationShort(new Date(lastSeenAt)),
          ns: 'common',
        })
    : '';

  return (
    <Row alignItems="center" gap="spacing.general.x1" minHeight={12}>
      <Text fontSize={12} color="text.placeholder" numberOfLines={1} style={{ maxWidth: '100%' }} variant="bodyBase">
        {trustPoints ? `👍 (${trustPoints.toFixed(0)})` : ''}
        {trustPoints && lastSeenAt ? ' | ' : ''}
        {lastActive}
      </Text>
    </Row>
  );
}

function DismissAction({
  progress,
  drag,
  theme,
  t,
  dismissed,
  onConfirm,
}: {
  progress: SharedValue<number>;
  drag: SharedValue<number>;
  theme: Theme;
  t: (key: string) => string;
  dismissed?: boolean;
  onConfirm: () => void;
}) {
  const labelStyle = useAnimatedStyle(() => {
    const absX = Math.abs(drag.get());
    const opacity = interpolate(absX, [50, 80], [0, 1], Extrapolation.CLAMP);
    const translateY = interpolate(absX, [50, 80], [6, 0], Extrapolation.CLAMP);
    return { opacity, transform: [{ translateY }] };
  });
  const { t: tA11y } = useTranslation('accessibility');

  const containerStyle = useAnimatedStyle(() => {
    const p = progress.get();
    const opacity = interpolate(p, [0, 0.3], [0, 1], Extrapolation.CLAMP);
    return { opacity };
  });

  return (
    <Animated.View
      style={[
        {
          width: '25%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: !dismissed ? `${theme.colors['surface.error.default']}8D` : theme.colors['text.placeholder'],
          borderRadius: theme.borderRadii['radius.lg'],
          marginLeft: theme.spacing['spacing.general.x1'],
        },
        containerStyle,
      ]}
    >
      {/* remove the onSwipeableOpen={doDismiss} in the swipeable if instead you want to handle the dismiss action here, with a Pressable */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={!dismissed ? tA11y('request.hide') : tA11y('request.restore')}
        onPress={onConfirm}
        style={{ alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
      >
        <Animated.View
          style={[{ alignItems: 'center', paddingHorizontal: theme.spacing['spacing.general.x10'] }, labelStyle]}
        >
          {!dismissed ? (
            <Text
              color="white"
              variant="headerXSmall"
              fontSize={14}
              fontWeight="700"
              numberOfLines={1}
              style={{ width: '100%', flexWrap: 'nowrap' }}
            >
              {t('dismissAction')}
            </Text>
          ) : (
            <Text
              color="white"
              variant="headerXSmall"
              fontSize={14}
              fontWeight="700"
              numberOfLines={1}
              style={{ width: '100%', flexWrap: 'nowrap' }}
            >
              {t('undismissAction')}
            </Text>
          )}
        </Animated.View>
      </Pressable>
    </Animated.View>
  );
}

function RequestDismissControl({
  canDismiss,
  dismissed,
  hideStyle,
  hitSlop,
  iconColor,
  onPress,
  restoreStyle,
  textColor,
}: {
  canDismiss: boolean;
  dismissed?: boolean;
  hideStyle: object;
  hitSlop: number;
  iconColor: string;
  onPress: () => void;
  restoreStyle: object;
  textColor: string;
}) {
  const { t } = useTranslation('all-listings');
  if (!canDismiss) return null;

  if (!dismissed) {
    return (
      <Pressable
        accessible
        accessibilityRole="button"
        accessibilityLabel={t('request.hide', { ns: 'accessibility' })}
        accessibilityHint={t('request.hideHint', { ns: 'accessibility' })}
        onPress={onPress}
        hitSlop={hitSlop}
        style={hideStyle}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={iconColor} />
      </Pressable>
    );
  }

  return (
    <Button onPress={onPress} style={restoreStyle}>
      <Text style={{ color: textColor }} variant="bodyBase" fontWeight="700">
        {t('undismissAction')}
      </Text>
    </Button>
  );
}

function RequestCardCounters({
  numComments,
  numInteractions,
  numViews,
}: Pick<Request, 'numComments' | 'numInteractions' | 'numViews'>) {
  return (
    <Row
      borderRadius="radius.sm"
      gap="spacing.general.x2"
      paddingHorizontal="spacing.general.x1"
      paddingVertical="spacing.general.x0,5"
    >
      <ViewsSectionOthersListings numViews={numViews} />
      <MessageSectionOthersListings numInteractions={numInteractions} />
      {}
      {showCommentsUI ? <CommentSectionOthersListings numComments={numComments} /> : null}
    </Row>
  );
}

function getStandardRequestCardLayout({
  cardStatus,
  isFirst,
  isRecommendation,
  routeName,
  theme,
}: {
  cardStatus: CardStatus;
  isFirst?: boolean;
  isRecommendation: boolean;
  routeName: string;
  theme: Theme;
}) {
  const constrainedWidth = routeName === 'landing-page' ? 340 : isRecommendation ? 320 : undefined;
  return {
    cardStyle: {
      marginTop: isFirst && cardStatus === 'found' ? theme.spacing['spacing.general.x1'] : undefined,
      maxWidth: constrainedWidth,
      minWidth: constrainedWidth,
    },
    columnBackground: isRecommendation ? ('surface.default' as const) : ('surface.secondary-neutral' as const),
    columnPadding: isRecommendation ? ('spacing.general.x3' as const) : ('spacing.general.x4' as const),
    columnMinWidth: isRecommendation ? 300 : 320,
    columnStyle: isRecommendation ? { borderColor: theme.colors['border.secondary'], borderWidth: 1 } : undefined,
  };
}

function RequestCardOwnerFooter({
  listing,
  showUser,
  theme,
}: {
  listing: ListingRequestCardProps['listing'];
  showUser?: boolean;
  theme: Theme;
}) {
  const { t } = useTranslation('all-listings');

  return (
    <>
      {showUser ? (
        <ListingRequestUserRow
          user={listing.user}
          groups={listing.groups}
          theme={theme}
          isCurrentlyUnavailable={listing.isCurrentlyUnavailable}
          countsSlot={
            <RequestCardCounters
              numComments={listing.numComments}
              numInteractions={listing.numInteractions}
              numViews={listing.numViews}
            />
          }
        />
      ) : null}
      {listing.lender ? (
        <LenderAcceptedLoan
          lender={listing.lender}
          t={t}
          style={{ marginTop: theme.spacing['spacing.general.x0,5'] }}
        />
      ) : null}
    </>
  );
}

function ListingRequestCardBody({
  animatedCardStyle,
  badgeBg,
  cardStatus,
  isFirst,
  listing,
  onDismiss,
  onLayout,
  onPress,
  variant,
}: {
  animatedCardStyle: ReturnType<typeof useAnimatedStyle>;
  badgeBg: string;
  cardStatus: CardStatus;
  isFirst?: boolean;
  listing: ListingRequestCardProps['listing'];
  onDismiss: () => void;
  onLayout: (height: number, width: number) => void;
  onPress: () => void;
  variant: RequestCardViewVariant & {
    animation: 'dismissing' | 'idle';
    layout: 'recommendation' | 'standard';
  };
}) {
  const { t } = useTranslation('all-listings');
  const theme = useTheme<Theme>();
  const route = useRoute();
  const { before, dismissed, distance, item, needMatches, picture, pictures, subtype } = listing;
  const thumbPicture = pictures?.[0]?.url ?? picture;
  const canDismiss = variant.dismissal === 'enabled';
  const hideDuplicateDonationBadge = variant.duplicateDonationBadge === 'hidden';
  const hideListingsSubtype = variant.listingSubtype === 'hidden';
  const isDismissAnimationActive = variant.animation === 'dismissing';
  const isRecommendation = variant.layout === 'recommendation';
  const showHauteGaspesieRequestMode = variant.modeBadge === 'visible';
  const showSubtypeBadge = variant.subtypeBadge === 'visible';
  const showUser = variant.user === 'visible';
  const layout = getStandardRequestCardLayout({ cardStatus, isFirst, isRecommendation, routeName: route.name, theme });

  return (
    <Animated.View
      onLayout={({ nativeEvent }) => onLayout(nativeEvent.layout.height, nativeEvent.layout.width)}
      style={[animatedCardStyle, isDismissAnimationActive ? { overflow: 'hidden' } : undefined]}
    >
      <Animated.View style={{ overflow: 'hidden' }}>
        <Pressable onPress={onPress} testID={`listing-request-card-${item.name}`} style={layout.cardStyle}>
          {dismissed ? (
            <Column
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1,
                backgroundColor: theme.colors['surface.tertiary-neutral'],
                height: '100%',
                borderRadius: theme.borderRadii['radius.lg'],
                opacity: 0.5,
              }}
            />
          ) : null}
          <Column
            backgroundColor={layout.columnBackground}
            borderRadius="radius.lg"
            padding={layout.columnPadding}
            minWidth={layout.columnMinWidth}
            style={layout.columnStyle}
          >
            <Row flex={1} gap="spacing.general.x4">
              <Column>
                {showSubtypeBadge ? <SubtypeBadge subtype={subtype} theme={theme} t={t} /> : null}
                <ListingThumbnail
                  picture={thumbPicture}
                  type={ListingType.request}
                  subtype={subtype}
                  skipDonationOverlay
                  size={55}
                  isFound={cardStatus === 'found'}
                />
                {showHauteGaspesieRequestMode ? (
                  <Row marginTop="spacing.general.x1">
                    <ListingCardModeBadge listing={listing} />
                  </Row>
                ) : null}
              </Column>

              <Column flex={1}>
                <Column gap="spacing.general.x2">
                  <Row gap="spacing.general.x1" alignItems="center" style={{ position: 'relative', width: '100%' }}>
                    {needMatches?.length ? <ConciergeSignal /> : null}
                    <Text fontWeight="700" numberOfLines={2} variant="bodyLarge" flex={1}>
                      {item.name}
                    </Text>
                    <RequestDismissControl
                      canDismiss={canDismiss}
                      dismissed={dismissed}
                      hideStyle={{
                        marginLeft: 'auto',
                        alignSelf: 'flex-start',
                        paddingVertical: theme.spacing['spacing.general.x1'],
                      }}
                      hitSlop={8}
                      iconColor={theme.colors['text.placeholder']}
                      onPress={onDismiss}
                      restoreStyle={{
                        marginLeft: 'auto',
                        alignSelf: 'flex-start',
                        paddingHorizontal: theme.spacing['spacing.general.x2'],
                        paddingVertical: theme.spacing['spacing.general.x1'],
                        borderRadius: theme.borderRadii['radius.sm'],
                        zIndex: 1,
                        flexShrink: 1,
                      }}
                      textColor={theme.colors['text.brand']}
                    />
                  </Row>

                  {hideDuplicateDonationBadge ? null : (
                    <StatusBadge
                      cardStatus={cardStatus}
                      badgeBg={badgeBg}
                      hideListingsSubtype={hideListingsSubtype}
                      before={before}
                      start={listing.start}
                      end={listing.end}
                    />
                  )}
                </Column>

                <Column marginTop="spacing.general.x2">
                  <RequestCardDistance distance={distance} />
                </Column>
              </Column>
            </Row>

            <RequestCardOwnerFooter listing={listing} showUser={showUser} theme={theme} />
          </Column>
        </Pressable>
      </Animated.View>
    </Animated.View>
  );
}

function ListingRequestCard(props: ListingRequestCardProps) {
  const navigation = useNavigation();
  const { t } = useTranslation('all-listings');
  const theme = useTheme<Theme>();
  const { isLargeUI } = useUISize();
  const route = useRoute();
  const { data: self } = useUser();
  const isGuestWeb = useIsGuestWeb();
  const isListingOwner = props.listing.userId === self?.id;
  const isRecommendation = props.presentation === 'recommendation';
  const canDismiss = !!self?.id && !isListingOwner && !isRecommendation;
  const showHauteGaspesieRequestMode = isWhiteLabel(WhiteLabel.HauteGaspesie);

  const withAuthorize = useAuthorize();

  const { listing, hideListingsSubtype: _hideListingsSubtype, isFirst, showUser, onBeforeDismiss } = props;
  const hideListingsSubtype = Boolean(_hideListingsSubtype);

  const { id, start, end, status, subtype, before, createdAt, lender, dismissed } = listing;

  const { mutate: dismiss } = useDismissListing();
  const { mutate: undismiss } = useUndismissListing();

  const { cardStatus, showSubtypeBadge, badgeBg } = useListingRequestCardBadge({
    subtype,
    status,
    start,
    end,
    createdAt,
    hideListingsSubtype,
    hasLender: !!lender,
    isDonation: listing.isDonation,
    transactionMode: listing.transactionMode,
  });
  const displayedBadgeBg =
    isRecommendation && cardStatus === 'activeSearch' ? theme.colors['surface.secondary-neutral'] : badgeBg;
  const hideDuplicateDonationBadge =
    showHauteGaspesieRequestMode &&
    cardStatus === 'donation' &&
    getDonationTextForOverlay(before).i18key === 'donation.label';

  const isSwipingRef = useRef(false);
  const isDismissingRef = useRef(false);
  const dismissAnimationKey = `${id}:${String(dismissed)}`;
  const [dismissAnimationState, setDismissAnimationState] = useState({
    key: dismissAnimationKey,
    isActive: false,
  });
  let currentDismissAnimationState = dismissAnimationState;
  if (dismissAnimationState.key !== dismissAnimationKey) {
    currentDismissAnimationState = { key: dismissAnimationKey, isActive: false };
    setDismissAnimationState(currentDismissAnimationState);
  }
  const isDismissAnimationActive = currentDismissAnimationState.isActive;
  const [measuredLayout, setMeasuredLayout] = useState<{ height: number; width: number }>();
  const opacity = useSharedValue(1);
  const collapseProgress = useSharedValue(1);
  const shouldAnimateDesktopDismiss = isLargeUI && !isMobileNative;
  const shouldAnimateMobileDismiss = isMobileNative && !isLargeUI && route.name === 'all-listings';
  const shouldAnimateLandingPageDismiss = route.name === 'landing-page';

  useEffect(() => {
    cancelAnimation(opacity);
    cancelAnimation(collapseProgress);
    opacity.set(1);
    collapseProgress.set(1);
    isDismissingRef.current = false;
  }, [collapseProgress, dismissed, id, opacity]);

  const animatedCardStyle = useAnimatedStyle(() => {
    const style: { height?: DimensionValue; opacity: number; width?: DimensionValue } = {
      opacity: opacity.get(),
    };

    if (shouldAnimateLandingPageDismiss) {
      style.width =
        isDismissAnimationActive && measuredLayout?.width ? measuredLayout.width * collapseProgress.get() : 'auto';
    }
    if (shouldAnimateMobileDismiss) {
      style.height =
        isDismissAnimationActive && measuredLayout?.height ? measuredLayout.height * collapseProgress.get() : 'auto';
    }

    return style;
  }, [isDismissAnimationActive, measuredLayout, shouldAnimateLandingPageDismiss, shouldAnimateMobileDismiss]);

  const goToListing = () => {
    if (isSwipingRef.current) return;
    if (isGuestWeb) {
      navigateToListing(navigation, id);
      return;
    }
    withAuthorize(() => navigateToListing(navigation, id));
  };

  const resetDismissAnimation = () => {
    cancelAnimation(opacity);
    cancelAnimation(collapseProgress);
    opacity.set(1);
    collapseProgress.set(1);
    isDismissingRef.current = false;
    setDismissAnimationState({ key: dismissAnimationKey, isActive: false });
  };

  const executeDismiss = () => {
    if (!canDismiss) return;
    if (!dismissed) {
      onBeforeDismiss?.();
      dismiss(id, { onError: resetDismissAnimation });
      if (route.name === 'landing-page') return;
      Toast.show({
        type: 'success',
        position: 'bottom',
        bottomOffset: 108,
        text1: t('dismissToast.title'),
        text2: t('dismissToast.undo'),
        visibilityTime: 5000,
        onPress: () => {
          onBeforeDismiss?.();
          resetDismissAnimation();
          undismiss(id, { onError: resetDismissAnimation });
          Toast.hide();
        },
      });
    } else {
      onBeforeDismiss?.();
      resetDismissAnimation();
      undismiss(id, { onError: resetDismissAnimation });
    }
  };

  const doDismiss = () => {
    if (!canDismiss || isDismissingRef.current) return;
    if (!dismissed && shouldAnimateDesktopDismiss) {
      isDismissingRef.current = true;
      opacity.set(
        withTiming(0, { duration: DESKTOP_DISMISS_FADE_DURATION }, (finished) => {
          if (finished) {
            scheduleOnRN(executeDismiss);
          }
        }),
      );
      return;
    }
    if (!dismissed && shouldAnimateMobileDismiss) {
      isDismissingRef.current = true;
      setDismissAnimationState({ key: dismissAnimationKey, isActive: true });
      opacity.set(
        withTiming(0, { duration: MOBILE_DISMISS_COLLAPSE_DURATION }, (finished) => {
          if (finished) {
            scheduleOnRN(executeDismiss);
          }
        }),
      );
      collapseProgress.set(withTiming(0, { duration: MOBILE_DISMISS_COLLAPSE_DURATION }));
      return;
    }
    if (!dismissed && shouldAnimateLandingPageDismiss) {
      isDismissingRef.current = true;
      setDismissAnimationState({ key: dismissAnimationKey, isActive: true });
      opacity.set(withTiming(0, { duration: LANDING_PAGE_DISMISS_COLLAPSE_DURATION }));
      collapseProgress.set(
        withTiming(0, { duration: LANDING_PAGE_DISMISS_COLLAPSE_DURATION }, (finished) => {
          if (finished) {
            scheduleOnRN(executeDismiss);
          }
        }),
      );
      return;
    }

    executeDismiss();
  };

  const renderDismissAction = (progress: SharedValue<number>, drag: SharedValue<number>) => (
    <DismissAction progress={progress} drag={drag} theme={theme} t={t} dismissed={dismissed} onConfirm={doDismiss} />
  );

  const isSwipeable = route.name === 'all-listings' && canDismiss && isMobileNative;
  const swipeableRef = useSwipeableReset([id, dismissed, isSwipeable]);
  const cardVariant = getListingRequestCardBodyVariant({
    canDismiss,
    hideDuplicateDonationBadge,
    hideListingsSubtype,
    isDismissAnimationActive,
    isRecommendation,
    showHauteGaspesieRequestMode,
    showSubtypeBadge,
    showUser,
  });

  const cardBody = (
    <ListingRequestCardBody
      animatedCardStyle={animatedCardStyle}
      badgeBg={displayedBadgeBg}
      cardStatus={cardStatus}
      isFirst={isFirst}
      listing={listing}
      onDismiss={doDismiss}
      onLayout={(height, width) => {
        if (isDismissingRef.current || (!shouldAnimateLandingPageDismiss && !shouldAnimateMobileDismiss)) return;
        if (height <= 0 || width <= 0) return;
        if (measuredLayout?.height === height && measuredLayout.width === width) return;
        setMeasuredLayout({ height, width });
      }}
      onPress={goToListing}
      variant={cardVariant}
    />
  );
  if (!isSwipeable) return cardBody;

  return (
    <Swipeable
      ref={swipeableRef}
      friction={2}
      renderRightActions={renderDismissAction}
      rightThreshold={80}
      onSwipeableWillOpen={() => {
        isSwipingRef.current = true;
      }}
      onSwipeableClose={() => {
        isSwipingRef.current = false;
      }}
      onSwipeableOpen={(_dir) => {
        swipeableRef.current?.close();
        doDismiss();
      }}
    >
      {cardBody}
    </Swipeable>
  );
}

interface HighestRequestCardViewProps {
  badgeBg: string;
  cardStatus: CardStatus;
  listing: ListingRequestCardHighestProps['listing'];
  onDismiss: () => void;
  onPress: () => void;
  subtypeColorLight: string;
  thumbPicture?: string | null;
  variant: RequestCardViewVariant;
}

function HighestRequestModalCard({
  badgeBg,
  cardStatus,
  listing,
  onPress,
  subtypeColorLight,
  thumbPicture,
  variant,
}: HighestRequestCardViewProps) {
  const { t } = useTranslation('all-listings');
  const theme = useTheme<Theme>();
  const hideDuplicateDonationBadge = variant.duplicateDonationBadge === 'hidden';
  const hideListingsSubtype = variant.listingSubtype === 'hidden';
  const showHauteGaspesieRequestMode = variant.modeBadge === 'visible';
  const showSubtypeBadge = variant.subtypeBadge === 'visible';
  const showUser = variant.user === 'visible';
  const cardHeight = showUser ? 120 : 100;

  return (
    <Pressable
      onPress={onPress}
      testID={`listing-request-card-${listing.item.name}`}
      style={{
        borderWidth: 1,
        padding: theme.spacing['spacing.general.x2'],
        borderColor: theme.colors['surface.disabled'],
        borderRadius: theme.borderRadii['radius.lg'],
      }}
    >
      <Row borderRadius="radius.lg" height={showHauteGaspesieRequestMode ? undefined : cardHeight}>
        <Column>
          <Column position="relative">
            {showSubtypeBadge ? (
              <Column
                alignItems="center"
                position="absolute"
                top={0}
                left={0}
                right={0}
                zIndex={1}
                style={{
                  paddingHorizontal: 4,
                  paddingVertical: 2,
                  borderRadius: theme.borderRadii['radius.sm'],
                  backgroundColor: subtypeColorLight,
                }}
              >
                <Text fontWeight="700" variant="bodyBase">
                  {t(`subtype.${listing.subtype}`)}
                </Text>
              </Column>
            ) : null}

            <ListingThumbnail
              picture={thumbPicture}
              type={ListingType.request}
              subtype={listing.subtype}
              skipDonationOverlay
              forcedObjectFit="cover"
              size={cardHeight}
              isFound={cardStatus === 'found'}
            />
          </Column>
          {showHauteGaspesieRequestMode ? (
            <Row marginTop="spacing.general.x1">
              <ListingCardModeBadge listing={listing} />
            </Row>
          ) : null}
        </Column>

        <Column flex={1} paddingLeft="spacing.general.x2" justifyContent="space-between">
          <Column>
            <Row marginBottom="spacing.general.x2" alignItems="center" gap="spacing.general.x1">
              {listing.needMatches?.length ? <ConciergeSignal /> : null}
              <Text fontWeight="700" numberOfLines={2} variant="bodyBase">
                {listing.item.name}
              </Text>
            </Row>

            {cardStatus !== 'activeSearch' && !hideDuplicateDonationBadge ? (
              <Row alignItems={cardStatus === 'found' ? 'flex-start' : 'flex-end'} flex={1}>
                <Row
                  flexShrink={1}
                  style={{
                    backgroundColor: badgeBg,
                    paddingVertical: 4,
                    paddingHorizontal: 6,
                    borderRadius: theme.borderRadii['radius.sm'],
                  }}
                >
                  <StatusBadgeContent
                    cardStatus={cardStatus}
                    before={listing.before}
                    hideListingsSubtype={hideListingsSubtype}
                  />
                  <DateRange cardStatus={cardStatus} start={listing.start} end={listing.end} />
                </Row>
              </Row>
            ) : null}
            <Row gap="spacing.general.x2" alignSelf="flex-start">
              <ViewsSectionOthersListings numViews={listing.numViews} />
              <MessageSectionOthersListings numInteractions={listing.numInteractions} />
            </Row>
            {listing.lender ? <LenderAcceptedLoan lender={listing.lender} t={t} /> : null}
          </Column>

          {showUser ? (
            <ListingRequestUserRow
              avatarSize={20}
              groups={listing.groups}
              isCurrentlyUnavailable={listing.isCurrentlyUnavailable}
              nameNumberOfLines={1}
              theme={theme}
              user={listing.user}
            />
          ) : null}
        </Column>
      </Row>
    </Pressable>
  );
}

function HighestRequestGridCard({
  animatedCardStyle,
  cardStatus,
  highestCardWidth,
  listing,
  onDismiss,
  onPress,
  responsiveCardWidth,
  thumbPicture,
  variant,
  verticalBadgeBg,
}: HighestRequestCardViewProps & {
  animatedCardStyle: ReturnType<typeof useAnimatedStyle>;
  highestCardWidth: number;
  responsiveCardWidth?: number;
  verticalBadgeBg: string;
}) {
  const { t } = useTranslation('all-listings');
  const theme = useTheme<Theme>();
  const canDismiss = variant.dismissal === 'enabled';
  const hideDuplicateDonationBadge = variant.duplicateDonationBadge === 'hidden';
  const hideListingsSubtype = variant.listingSubtype === 'hidden';
  const showHauteGaspesieRequestMode = variant.modeBadge === 'visible';
  const showSubtypeBadge = variant.subtypeBadge === 'visible';
  const showUser = variant.user === 'visible';

  return (
    <Animated.View style={animatedCardStyle}>
      <Pressable
        onPress={onPress}
        testID={`listing-request-card-${listing.item.name}`}
        style={{
          overflow: 'hidden',
          width: responsiveCardWidth ? '100%' : 220,
          marginHorizontal: responsiveCardWidth ? 0 : 8,
          marginVertical: 16,
        }}
      >
        <Row borderRadius="radius.lg">
          <Column flex={1}>
            {showUser ? (
              <ListingRequestUserRow
                avatarSize={30}
                groups={listing.groups}
                isCurrentlyUnavailable={listing.isCurrentlyUnavailable}
                nameNumberOfLines={1}
                theme={theme}
                user={listing.user}
                userRowPlacement="aboveThumbnail"
                trailingSlot={
                  <RequestDismissControl
                    canDismiss={canDismiss}
                    dismissed={listing.dismissed}
                    hideStyle={{
                      paddingVertical: theme.spacing['spacing.general.x1'],
                      alignSelf: 'flex-start',
                    }}
                    hitSlop={16}
                    iconColor={theme.colors['text.primary']}
                    onPress={onDismiss}
                    restoreStyle={{
                      alignSelf: 'flex-start',
                      paddingHorizontal: theme.spacing['spacing.general.x2'],
                      paddingVertical: theme.spacing['spacing.general.x1'],
                      borderRadius: theme.borderRadii['radius.sm'],
                      flexShrink: 1,
                    }}
                    textColor={theme.colors['text.brand']}
                  />
                }
              />
            ) : null}
            <Column
              position="relative"
              marginBottom={showHauteGaspesieRequestMode ? 'spacing.general.x1' : 'spacing.general.x2'}
            >
              {showSubtypeBadge ? <SubtypeBadge subtype={listing.subtype} theme={theme} t={t} /> : null}

              <View style={listing.dismissed ? { opacity: 0.5 } : undefined}>
                <ListingThumbnail
                  picture={thumbPicture}
                  type={ListingType.request}
                  subtype={listing.subtype}
                  skipDonationOverlay
                  forcedObjectFit="cover"
                  size={highestCardWidth}
                  isFound={cardStatus === 'found'}
                />
              </View>
              <RequestDismissControl
                canDismiss={!showUser && canDismiss}
                dismissed={listing.dismissed}
                hideStyle={{
                  position: 'absolute',
                  top: theme.spacing['spacing.general.x2'],
                  right: theme.spacing['spacing.general.x2'],
                  paddingVertical: theme.spacing['spacing.general.x1'],
                }}
                hitSlop={16}
                iconColor={theme.colors['text.primary']}
                onPress={onDismiss}
                restoreStyle={{
                  position: 'absolute',
                  top: theme.spacing['spacing.general.x2'],
                  right: theme.spacing['spacing.general.x2'],
                  opacity: 1,
                  paddingHorizontal: theme.spacing['spacing.general.x2'],
                  paddingVertical: theme.spacing['spacing.general.x1'],
                  borderRadius: theme.borderRadii['radius.sm'],
                  flexShrink: 1,
                }}
                textColor={theme.colors['text.brand']}
              />
              <Row
                backgroundColor="surface.primary-neutral"
                borderRadius="radius.sm"
                gap="spacing.general.x2"
                alignSelf="flex-end"
                paddingVertical="spacing.general.x0,5"
                paddingHorizontal="spacing.general.x1"
                style={{
                  position: 'absolute',
                  bottom: theme.spacing['spacing.general.x3'],
                  right: theme.spacing['spacing.general.x3'],
                }}
              >
                <ViewsSectionOthersListings numViews={listing.numViews} />
                <MessageSectionOthersListings numInteractions={listing.numInteractions} />
              </Row>
            </Column>

            {showHauteGaspesieRequestMode ? (
              <Row marginBottom="spacing.general.x2">
                <ListingCardModeBadge listing={listing} />
              </Row>
            ) : null}

            <Row marginBottom="spacing.general.x2" alignItems="center" gap="spacing.general.x1">
              {listing.needMatches?.length ? <ConciergeSignal /> : null}
              <Text fontWeight="700" numberOfLines={3} variant="bodyLarge">
                {listing.item.name}
              </Text>
            </Row>

            <Column gap="spacing.general.x2">
              {hideDuplicateDonationBadge ? null : (
                <Row alignItems="flex-end" flex={1}>
                  <Row
                    flexShrink={1}
                    style={{
                      backgroundColor: verticalBadgeBg,
                      paddingVertical: 4,
                      paddingHorizontal: 6,
                      borderRadius: theme.borderRadii['radius.sm'],
                      flexWrap: 'wrap',
                    }}
                  >
                    <StatusBadgeContent
                      cardStatus={cardStatus}
                      before={listing.before}
                      hideListingsSubtype={hideListingsSubtype}
                    />
                    <DateRange cardStatus={cardStatus} start={listing.start} end={listing.end} />
                  </Row>
                </Row>
              )}
              <RequestCardDistance distance={listing.distance} />
            </Column>
          </Column>
        </Row>
      </Pressable>
    </Animated.View>
  );
}

function ListingRequestCardHighest(props: ListingRequestCardHighestProps) {
  const navigation = useNavigation();
  const { t } = useTranslation('all-listings');
  const theme = useTheme<Theme>();
  const { isLargeUI } = useUISize();
  const isGuestWeb = useIsGuestWeb();

  const withAuthorize = useAuthorize();

  const {
    listing,
    showUser,
    hideListingsSubtype: _hideListingsSubtype,
    isInModal,
    columnWidth,
    onBeforeDismiss,
  } = props;
  const { id, start, end, status, picture, pictures, subtype, before, createdAt, lender, dismissed } = listing;
  const thumbPicture = pictures?.[0]?.url ?? picture;

  const { data: self } = useUser();
  const isListingOwner = listing.userId === self?.id;
  const canDismiss = !!self?.id && !isListingOwner;
  const showHauteGaspesieRequestMode = isWhiteLabel(WhiteLabel.HauteGaspesie);
  const hideListingsSubtype = _hideListingsSubtype ?? false;
  const { mutate: dismiss } = useDismissListing();
  const { mutate: undismiss } = useUndismissListing();
  const route = useRoute();
  const opacity = useSharedValue(1);
  const isDismissingRef = useRef(false);
  const shouldAnimateDesktopDismiss = isLargeUI && !isMobileNative;

  useEffect(() => {
    opacity.set(1);
    isDismissingRef.current = false;
  }, [dismissed, id, opacity]);

  const animatedCardStyle = useAnimatedStyle(() => ({ opacity: opacity.get() }));
  const resetDismissAnimation = () => {
    opacity.set(1);
    isDismissingRef.current = false;
  };

  const executeDismiss = () => {
    if (!canDismiss) return;
    if (!dismissed) {
      onBeforeDismiss?.();
      dismiss(id, { onError: resetDismissAnimation });
      if (route.name === 'landing-page') return;
      Toast.show({
        type: 'success',
        position: 'bottom',
        bottomOffset: 108,
        text1: t('dismissToast.title'),
        text2: t('dismissToast.undo'),
        visibilityTime: 5000,
        onPress: () => {
          onBeforeDismiss?.();
          undismiss(id, { onError: resetDismissAnimation });
          Toast.hide();
        },
      });
    } else {
      onBeforeDismiss?.();
      undismiss(id, { onError: resetDismissAnimation });
    }
  };

  const doDismiss = () => {
    if (!canDismiss || isDismissingRef.current) return;
    if (!dismissed && shouldAnimateDesktopDismiss) {
      isDismissingRef.current = true;
      opacity.set(
        withTiming(0, { duration: DESKTOP_DISMISS_FADE_DURATION }, (finished) => {
          if (finished) {
            scheduleOnRN(executeDismiss);
          }
        }),
      );
      return;
    }

    executeDismiss();
  };

  const { cardStatus, showSubtypeBadge, badgeBg } = useListingRequestCardBadge({
    subtype,
    status,
    start,
    end,
    createdAt,
    hideListingsSubtype,
    hasLender: !!lender,
    isDonation: listing.isDonation,
    transactionMode: listing.transactionMode,
  });
  const verticalBadgeBg = cardStatus === 'activeSearch' ? theme.colors['surface.secondary-neutral'] : badgeBg;
  const hideDuplicateDonationBadge =
    showHauteGaspesieRequestMode &&
    cardStatus === 'donation' &&
    getDonationTextForOverlay(before).i18key === 'donation.label';

  const goToListing = () => {
    if (isGuestWeb) {
      navigateToListing(navigation, id);
      return;
    }
    withAuthorize(() => navigateToListing(navigation, id));
  };

  const subtypeColorLight = getSubtypeColor(subtype);
  const responsiveCardWidth = typeof columnWidth === 'number' ? columnWidth : undefined;
  const highestCardWidth = responsiveCardWidth ?? 220;
  const cardVariant = getRequestCardViewVariant({
    canDismiss,
    hideDuplicateDonationBadge,
    hideListingsSubtype,
    showHauteGaspesieRequestMode,
    showSubtypeBadge,
    showUser,
  });

  if (isInModal) {
    return (
      <HighestRequestModalCard
        badgeBg={badgeBg}
        cardStatus={cardStatus}
        listing={listing}
        onDismiss={doDismiss}
        onPress={goToListing}
        subtypeColorLight={subtypeColorLight}
        thumbPicture={thumbPicture}
        variant={cardVariant}
      />
    );
  }

  return (
    <HighestRequestGridCard
      animatedCardStyle={animatedCardStyle}
      badgeBg={badgeBg}
      cardStatus={cardStatus}
      highestCardWidth={highestCardWidth}
      listing={listing}
      onDismiss={doDismiss}
      onPress={goToListing}
      responsiveCardWidth={responsiveCardWidth}
      subtypeColorLight={subtypeColorLight}
      thumbPicture={thumbPicture}
      variant={cardVariant}
      verticalBadgeBg={verticalBadgeBg}
    />
  );
}

export { ListingRequestCard, ListingRequestCardHighest };
